import { CheckCircle2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

function renderBoldText(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      const inner = part.slice(2, -2);
      return (
        <strong className="font-bold" key={inner}>
          {inner}
        </strong>
      );
    }
    return part;
  });
}

interface Option {
  id: string;
  text: string;
}

interface QuestionCardProps {
  question: string;
  options: Option[];
  selectedAnswer: string | null;
  onSelectAnswer: (optionId: string) => void;
  onAutoNext?: () => void;
}

export function QuestionCard({
  question,
  options,
  selectedAnswer,
  onSelectAnswer,
  onAutoNext,
}: QuestionCardProps) {
  const [autoAdvancingAnswer, setAutoAdvancingAnswer] = useState<string | null>(
    null,
  );
  const autoNextTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestOnAutoNextRef = useRef(onAutoNext);
  const isAutoAdvancing = autoAdvancingAnswer !== null;

  useEffect(() => {
    latestOnAutoNextRef.current = onAutoNext;
  }, [onAutoNext]);

  useEffect(() => {
    setAutoAdvancingAnswer(null);
    if (autoNextTimeoutRef.current) {
      clearTimeout(autoNextTimeoutRef.current);
      autoNextTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (autoNextTimeoutRef.current) {
        clearTimeout(autoNextTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="w-full max-w-2xl mx-auto px-[0px] pt-[0px] pb-[12px]">
      <h2
        className={`mb-8 text-center font-[Roboto_Slab] text-[18px] transition-all duration-300 ${
          isAutoAdvancing ? "translate-y-[-2px] opacity-80" : "opacity-100"
        }`}
      >
        {question}
      </h2>
      <div className="space-y-3">
        {options.map((option) => {
          const isSelected = selectedAnswer === option.id;
          const isConfirming = autoAdvancingAnswer === option.id;

          return (
            <button
              type="button"
              key={option.id}
              disabled={isAutoAdvancing}
              onClick={() => {
                if (isAutoAdvancing) return;

                onSelectAnswer(option.id);

                if (!onAutoNext) return;

                setAutoAdvancingAnswer(option.id);
                autoNextTimeoutRef.current = setTimeout(() => {
                  autoNextTimeoutRef.current = null;
                  setAutoAdvancingAnswer(null);
                  latestOnAutoNextRef.current?.();
                }, 550);
              }}
              className={`w-full text-left border-2 transition-all duration-300 rounded-[0px] disabled:cursor-default ${
                isSelected ? "bg-blue-50" : "bg-white"
              } ${isConfirming ? "scale-[1.01] ring-2 ring-[#FF00D2]/25" : ""} p-[12px]`}
              style={{
                borderColor: isSelected ? "#FF00D2" : "#e5e7eb",
              }}
              onMouseEnter={(e) => {
                if (!isAutoAdvancing) {
                  e.currentTarget.style.borderColor = "#FF00D2";
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = isSelected
                  ? "#FF00D2"
                  : "#e5e7eb";
              }}
            >
              <div className="flex items-center justify-between">
                <span className="font-light flex-1 text-left font-[Roboto]">
                  {renderBoldText(option.text)}
                </span>
                {isSelected && (
                  <CheckCircle2
                    className={`w-5 h-5 transition-all duration-300 ${
                      isConfirming ? "scale-110" : ""
                    }`}
                    style={{ color: "#FF00D2" }}
                  />
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
